import { fetchInstance } from "@/utils/fetchInstance";
import type {
  CertificateAttemptResponse,
  CertificateOrderResponse,
  CertificateVerification,
  CreateCertificateOrderBody,
  FinishCertificateExamResponse,
  PlatformCertificateStatus,
  RegisterCertificateBody,
  RegisterCertificateResponse,
  SubmitCertificateAnswerBody,
} from "@/models/certificate/PlatformCertificate";

/**
 * The paid certificate of a vitrin course, issued by Amoozim or a university.
 * Not gated by the mini-app's subscription.
 * See docs/platform-certificate-frontend-guide (8).md.
 */
export class PlatformCertificateService {
  private static base(miniAppUuid: string, courseId: string | number) {
    return `mini-apps/${miniAppUuid}/courses/${courseId}/platform-certificate`;
  }

  /** One certificate on the course — every write and the exam live under it. */
  private static offering(
    miniAppUuid: string,
    courseId: string | number,
    offeringId: string | number,
  ) {
    return `${this.base(miniAppUuid, courseId)}/${offeringId}`;
  }

  /** Every certificate the course sells, in one call. 404 → it sells none. */
  static getStatus(miniAppUuid: string, courseId: string | number) {
    return fetchInstance<PlatformCertificateStatus>(this.base(miniAppUuid, courseId), {
      method: "GET",
      auth: true,
    });
  }

  static register(
    miniAppUuid: string,
    courseId: string | number,
    offeringId: string | number,
    body: RegisterCertificateBody,
  ) {
    return fetchInstance<RegisterCertificateResponse, RegisterCertificateBody>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/register`,
      { method: "POST", auth: true, body },
    );
  }

  /** All three payments: 1 exam retry · 2 issuance · 3 re-post a returned parcel. */
  static createOrder(
    miniAppUuid: string,
    courseId: string | number,
    offeringId: string | number,
    body: CreateCertificateOrderBody,
  ) {
    return fetchInstance<CertificateOrderResponse, CreateCertificateOrderBody>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/orders`,
      { method: "POST", auth: true, body },
    );
  }

  /** Starts a sitting, or returns the one already in progress. */
  static startExam(miniAppUuid: string, courseId: string | number, offeringId: string | number) {
    return fetchInstance<CertificateAttemptResponse>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/exam/start`,
      { method: "POST", auth: true },
    );
  }

  /** 404 when nothing is in progress. */
  static getCurrentAttempt(
    miniAppUuid: string,
    courseId: string | number,
    offeringId: string | number,
  ) {
    return fetchInstance<CertificateAttemptResponse>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/exam/current-attempt`,
      { method: "GET", auth: true },
    );
  }

  static submitAnswer(
    miniAppUuid: string,
    courseId: string | number,
    offeringId: string | number,
    body: SubmitCertificateAnswerBody,
  ) {
    return fetchInstance<null, SubmitCertificateAnswerBody>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/exam/submit-answer`,
      { method: "POST", auth: true, body },
    );
  }

  static finishExam(miniAppUuid: string, courseId: string | number, offeringId: string | number) {
    return fetchInstance<FinishCertificateExamResponse>(
      `${this.offering(miniAppUuid, courseId, offeringId)}/exam/finish`,
      { method: "POST", auth: true },
    );
  }

  /**
   * Public, no auth — where the QR on the certificate points. Takes the
   * verification code only; an unknown code is a 200 with `found: false`.
   */
  static verify(code: string) {
    return fetchInstance<CertificateVerification>(
      `certificates/verify/${encodeURIComponent(code)}`,
      { method: "GET" },
    );
  }
}
